/**
 * The exact request bodies this feature sends (design B.5).
 *
 * Pure builders: they take the loaded object plus the dirty diff and return the
 * body, or `null` when there is nothing to send. Nothing here talks to the API;
 * nothing here decides whether a save is needed — the page does that (C.6).
 *
 * Every rule below is a rule the API forces, not a preference:
 *
 * - `translations` is DELETED from the enrich body, never nulled. Eight of the
 *   nine handlers carry the stored map forward only when the request OMITS the
 *   key (`supplied ?? stored`), so an explicit `{}` wipes it and an explicit
 *   `null` is indistinguishable from absent — except on geodata, where it is
 *   not absent at all.
 * - `taskName` is echoed nested AND top-level. Every handler does
 *   `enrichment.TaskName = request.TaskName` unconditionally, so a save that
 *   omits it nulls the Tina job pointer and breaks the entity trace.
 * - `enrichmentStatus`, `callerDrivesEmbedding` and `enrichmentSources` are
 *   never sent: absent status means `Enriched`, absent `callerDrivesEmbedding`
 *   is the curator branch, and `enrichmentSources` is a 400 on the three
 *   entities that have it without `callerDrivesEmbedding`.
 */
import { withClearedFields } from "@/lib/utils";

import type { EnrichmentPageConfig } from "./config";
import { emptyValueForControl, isPlainObject } from "./field-rules";
import {
  type TranslationBlock,
  type TranslationLocale,
  type TranslationMap,
  translationsPatchBody,
} from "./translations";

/** `{ en: [], el: [], ar: [], ro: [] }`, with any stored member preserved. */
function normaliseTransliterations(value: unknown): Record<string, unknown> {
  const source = isPlainObject(value) ? value : {};
  return {
    ...source,
    en: source.en ?? [],
    el: source.el ?? [],
    ar: source.ar ?? [],
    ro: source.ro ?? [],
  };
}

/**
 * The keys of the flat (genre) enrich payload, in the order B.5 lists them.
 *
 * Genre's handler merges per field and accepts no `taskName`, no `reasoning`
 * and no `translations`, so a body carrying them would be pointless at best.
 */
export const FLAT_ENRICH_KEYS = [
  "about",
  "aliases",
  "parents",
  "features",
  "transliterations",
] as const;

export interface EnrichBodyInput {
  id: string;
  /**
   * The object exactly as loaded: the WHOLE enrichment object for the nested
   * shape, the enrichment slice of the document for the flat shape.
   */
  loaded: Record<string, unknown>;
  /**
   * The dirty-only values, as `getDirtyValues(dirtyFields, values)` returns
   * them — keys AutoForm deleted because the curator emptied them are ABSENT
   * here by design (see `withClearedFields`).
   */
  changed?: Record<string, unknown>;
  /** The RAW react-hook-form values, for the cleared-key recovery. */
  rawValues?: Record<string, unknown>;
  /** `""` is only safe for these keys. Defaults to none. */
  clearableKeys?: Iterable<string>;
}

export interface EnrichBody {
  id: string;
  enrichment: Record<string, unknown>;
  /** The envelope copy of `taskName` — the one that actually persists. */
  taskName?: string | null;
}

/**
 * The `PUT /{path}/enrich` body: the whole loaded object with the dirty keys
 * merged in, minus the keys the API must not receive.
 */
export function buildEnrichBody(
  config: EnrichmentPageConfig,
  input: EnrichBodyInput,
): EnrichBody {
  const { id, loaded, changed = {}, rawValues, clearableKeys } = input;

  const merged: Record<string, unknown> = { ...loaded };
  Object.assign(
    merged,
    withClearedFields(changed, rawValues ?? {}, loaded, clearableKeys ?? []),
  );

  // `enrichmentSources` has no `[FirestoreProperty]` anywhere: it lives in an
  // `enrichment/sources` subcollection and reads back null from the parent
  // document. Sending it on artists/venues/events is a 400.
  delete merged.enrichmentSources;

  // B.5 rule 1: delete, never null. A present empty `{}` wins the handler's
  // `??` and wipes the stored map.
  delete merged.translations;

  if (config.hasTransliterations) {
    merged.transliterations = normaliseTransliterations(
      merged.transliterations,
    );
  } else {
    delete merged.transliterations;
  }

  fillRequiredGaps(config, merged);

  if (config.shape === "flat") {
    // Genre's handler merges per field and `FirestoreUpdateBuilder.Set` skips
    // nulls, so a member that is absent from the payload is left alone rather
    // than cleared. The five are always written anyway: they are the payload.
    const payload: Record<string, unknown> = {};
    for (const key of FLAT_ENRICH_KEYS) payload[key] = merged[key];
    if (typeof payload.about !== "string") payload.about = "";
    if (!Array.isArray(payload.aliases)) payload.aliases = [];
    if (!Array.isArray(payload.parents)) payload.parents = [];
    if (!isPlainObject(payload.features)) payload.features = {};
    return { id, enrichment: payload };
  }

  if (!config.hasTaskName) {
    delete merged.taskName;
    return { id, enrichment: merged };
  }

  const taskName = (merged.taskName ?? null) as string | null;
  merged.taskName = taskName;
  return { id, enrichment: merged, taskName };
}

/**
 * Write the empty value of every required member that carries nothing.
 *
 * The enrich PUT full-replaces (or, on geodata, writes explicit nulls), so a
 * required member the object does not carry is either destroyed or a 400.
 * `""` and `[]` are what the DTO's own defaults are.
 */
function fillRequiredGaps(
  config: EnrichmentPageConfig,
  target: Record<string, unknown>,
): void {
  if (config.shape === "flat") return;
  for (const rule of config.fields) {
    if (!rule.required) continue;
    if (target[rule.key] !== undefined && target[rule.key] !== null) continue;
    const empty = emptyValueForControl(rule.control);
    if (empty !== undefined) target[rule.key] = empty;
  }
}

export interface TranslationsBody {
  id: string;
  translations: Partial<Record<TranslationLocale, TranslationBlock>>;
}

/**
 * The `PUT /{path}/enrichment-translations` body, or `null` when no locale
 * differs from what was stored.
 *
 * Sending `"translations": {}` would be a 200 no-op, so the caller skips the
 * round trip instead. Only dirty locales are included; every other locale is
 * untouched by construction.
 */
export function buildTranslationsBody(
  id: string,
  stored: TranslationMap | null | undefined,
  draft: TranslationMap | null | undefined,
): TranslationsBody | null {
  // `dirtyLocales` (through `translationsPatchBody`) is the single diff: absent
  // locales are untouched, a blank never-stored locale is dropped, and a stored
  // locale that is now blank goes out as `{}`.
  const translations = translationsPatchBody(stored, draft);
  if (Object.keys(translations).length === 0) return null;
  return { id, translations };
}
